import { FC, KeyboardEvent, useRef, useState } from "react";

import { Box, Stack } from "@mui/material";
import { hsl, hslToHex } from "@simplyk/common";
import { ColorResult } from "react-color";

import { getEyeDropper } from "./helpers/eyeDropper";
import { HueSlider } from "./HueSlider/HueSlider";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Eyedropper } from "@/icons/outlined";

const COMPLETE_HEX_REGEX = /^#[A-F0-9]{6}$/;
const SHORT_HEX_REGEX = /^#[A-F0-9]{3}$/;
const FORM_BUTTON_SATURATION = 80;
const FORM_BUTTON_LIGHTNESS = 40;

interface InlineColorPickerProps {
  color: string;
  onPreview: (color: string) => void;
  onCommit: (color: string) => void;
  shouldFocusHexInput?: boolean;
}

export const InlineColorPicker: FC<InlineColorPickerProps> = ({
  color,
  onPreview,
  onCommit,
  shouldFocusHexInput = false,
}) => {
  const { t } = useTranslate();
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const latestDraggedColor = useRef(color);
  const EyeDropper = getEyeDropper();

  const hexValue = hexDraft ?? color.toUpperCase();
  const isHexDraftInvalid = hexDraft !== null && hexDraft !== "#" && !toCompleteHex(hexDraft);

  const handleHueChange = ({ hsl: { h } }: ColorResult) => {
    const hex = hslToHex(hsl(Math.round(h), FORM_BUTTON_SATURATION, FORM_BUTTON_LIGHTNESS));
    latestDraggedColor.current = hex;
    setHexDraft(null);
    onPreview(hex);
  };

  const handleHuePointerDown = () => {
    const colorBeforeDrag = color;
    latestDraggedColor.current = colorBeforeDrag;

    const endDrag = (event: PointerEvent) => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      if (event.type === "pointerup") {
        onCommit(latestDraggedColor.current);
      } else {
        onPreview(colorBeforeDrag);
      }
    };

    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  };

  const handleHexChange = (value: string) => {
    const hexWithoutSpaces = value.replace(/\s/g, "").toUpperCase();
    const nextHexDraft = hexWithoutSpaces.startsWith("#") ? hexWithoutSpaces : `#${hexWithoutSpaces}`;
    setHexDraft(nextHexDraft);
    if (COMPLETE_HEX_REGEX.test(nextHexDraft)) {
      onCommit(nextHexDraft);
    }
  };

  const handleHexBlur = () => {
    const completeHex = hexDraft === null ? undefined : toCompleteHex(hexDraft);
    if (completeHex) {
      onCommit(completeHex);
    }
    setHexDraft(null);
  };

  const handleHexKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
      event.target.blur();
    }
  };

  const handleEyeDropperClick = () => {
    if (!EyeDropper) {
      return;
    }
    new EyeDropper()
      .open()
      .then(({ sRGBHex }) => onCommit(sRGBHex.toUpperCase()))
      .catch(() => undefined);
  };

  return (
    <Stack
      gap={2}
      sx={(theme) => ({
        p: 2,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
      })}
    >
      <Box onPointerDown={handleHuePointerDown} sx={{ touchAction: "none" }}>
        <HueSlider color={color} onChange={handleHueChange} />
      </Box>

      <Stack direction="row" gap={1} alignItems="center">
        <Box
          sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: 0.75, bgcolor: color }}
          data-test="custom-color-swatch"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DashboardTextField
            autoFocus={shouldFocusHexInput}
            aria-label={t("dashboard", "liveFormEditor.stepColor.hexValue")}
            value={hexValue}
            onChange={(event) => handleHexChange(event.target.value)}
            onBlur={handleHexBlur}
            onKeyDown={handleHexKeyDown}
            error={isHexDraftInvalid}
            data-test="custom-color-hex-input"
          />
        </Box>
        {EyeDropper ? (
          <DashboardIconButton
            vibe="neutral"
            variant="outlined"
            size="medium"
            onClick={handleEyeDropperClick}
            sx={{ flexShrink: 0 }}
            aria-label={t("dashboard", "liveFormEditor.stepColor.eyedropper")}
            data-test="custom-color-eyedropper-button"
          >
            <Eyedropper />
          </DashboardIconButton>
        ) : null}
      </Stack>
      {isHexDraftInvalid ? (
        <DashboardTypography variant="caption" vibe="text-danger-intense">
          {t("dashboard", "liveFormEditor.stepColor.hexValue.invalid")}
        </DashboardTypography>
      ) : null}
      <DashboardTypography variant="caption" vibe="text-neutral-quiet">
        {t("dashboard", "liveFormEditor.stepColor.contrastAdjustmentNote")}
      </DashboardTypography>
    </Stack>
  );
};

const toCompleteHex = (hexDraft: string) => {
  if (COMPLETE_HEX_REGEX.test(hexDraft)) {
    return hexDraft;
  }
  if (SHORT_HEX_REGEX.test(hexDraft)) {
    return `#${[...hexDraft.slice(1)].map((digit) => digit + digit).join("")}`;
  }
  return undefined;
};
